import BaseSubscription from './BaseSubscription';
import { gql, sendAPIRequest } from './GraphQLClient';
import { logInfo, logError } from './services';
import { getUserFullName } from './../utils/utilsChat';
import { getFromLocalStorage, setToLocalStorage } from './../utils/utilsGlobal';

export const PUSH_NOTIFICATIONS_FORCE_NAVIGATE = 'PUSH_NOTIFICATIONS_FORCE_NAVIGATE';
export const PUSH_NOTIFICATIONS_FORCE_SCROLL_TO_MESSAGE = 'PUSH_NOTIFICATIONS_FORCE_SCROLL_TO_MESSAGE';
export const PUSH_NOTIFICATIONS_SYNC_NEW_MESSAGE_WITH_APP = 'PUSH_NOTIFICATIONS_SYNC_NEW_MESSAGE_WITH_APP';


export default class PushNotifications extends BaseSubscription {

  browserSupports = {
    notifications: false,
    push: false,
  };

  serviceWorkerRegistration = null;

  constructor(){
    super();
    this.browserSupports = this.detectBrowserSupport();

    if (this.browserSupports.notifications) {
      this.requestPermissionsIfNeeded();
      navigator.serviceWorker.addEventListener('message', this.onServiceWorkerMessage);
    }
  }

  passServiceWorkerRegistration = (serviceWorkerRegistration) => {
    this.serviceWorkerRegistration = serviceWorkerRegistration;
  };

  onServiceWorkerMessage = (e) => {
    const data = e?.data || {};
    if (data.eventType === 'navigateTo') {
      this.triggerEvent(PUSH_NOTIFICATIONS_FORCE_NAVIGATE, data.targetPath);
    }
    else if (data.eventType === 'scrollToMessage') {
      this.triggerEvent(PUSH_NOTIFICATIONS_FORCE_SCROLL_TO_MESSAGE, data.messageId);
    }
    else if (data.eventType === 'syncNewMessageWithApp') {
      this.triggerEvent(PUSH_NOTIFICATIONS_SYNC_NEW_MESSAGE_WITH_APP, data.message);
    }
    else if (data.eventType === 'resubscribeToPushNotifications') {
      this.removeCurrentPushSubscription().finally(() => {
        this.generatePushSubscription().then(this.savePushSubscription);
      });
    }
  };

  detectBrowserSupport(){
    return {
      notifications: 'Notification' in window,
      push: 'serviceWorker' in navigator && 'PushManager' in window,
    }
  };

  requestPermissionsIfNeeded(){
    return new Promise((resolve, reject) => {
      if (Notification.permission === 'granted') {
        resolve(Notification.permission);
        return;
      }
      Notification.requestPermission(result => {
        if (result === 'granted') {
          logInfo('PushNotifications: Notification permissions granted');
          resolve(Notification.permission);
        }
        else {
          logError('PushNotifications: Notification permissions rejected', { error: result });
          reject(Notification.permission);
        }
      });
    });
  };

  generateNotificationText(message){
    const attachmentNames = (message.attachments || []).map(attachment => attachment.name).join(', ');
    const mentions = (message.mentions || []).map(mention => {
      return mention.value === 'ALL' ? '@Все' : '@' + getUserFullName(mention.client);
    }).join(', ');
    return (mentions ? mentions + ' ' : '') + message.text + (attachmentNames ? ', ' + attachmentNames : '');
  };

  showNotification = (message) => {
    if (this.browserSupports.push) {
      this.showServiceWorkerNotification(message);
    }
    else {
      this.showLocalNotification(message);
    }
  };

  showServiceWorkerNotification = (message) => {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        eventType: 'showNotification',
        message,
      });
    }
  };

  showLocalNotification = (message) => {
    if (!this.browserSupports.notifications) {
      return;
    }
    const title = message.room.title;
    const options = {
      body: this.generateNotificationText(message),
      tag: 'message',
    };
    const notification = new Notification(title, options);
    notification.onclick = () => {
      notification.close();
    };
  };

  urlB64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = self.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  generatePushSubscription = () => {
    if (!this.serviceWorkerRegistration) {
      logError('PushNotifications: Unable to generate push subscription: no serviceWorkerRegistration found');
      return Promise.reject(null);
    }
    if (!this.serviceWorkerRegistration?.pushManager?.subscribe) {
      logError('PushNotifications: Unable to generate push subscription: Push API not supported');
      return Promise.reject(null);
    }
    const options = {
      applicationServerKey: this.urlB64ToUint8Array(process.env.WEB_PUSH_PUBLIC_KEY),
      userVisibleOnly: true,
    };
    return this.serviceWorkerRegistration.pushManager
      .subscribe(options)
      .then(subscription => subscription.toJSON());
  };

  subscribeToPushNotifications = (currentOperatorId) => {
    const currentPushSubscription = getFromLocalStorage('PUSH_SUBSCRIPTION', {});
    const isAlreadySubscribed = currentPushSubscription.operatorId === currentOperatorId;

    if (isAlreadySubscribed) {
      logInfo('PushNotifications: Already subscribed to push notifications', currentPushSubscription);
    }
    else {
      this.removeCurrentPushSubscription().finally(() => {
        this.generatePushSubscription().then(subscription => {
          this.savePushSubscription(subscription, currentOperatorId);
        });
      });
    }
  };

  savePushSubscription = (subscription, operatorId) => {
    setToLocalStorage('PUSH_SUBSCRIPTION', { operatorId, subscription });

    const query = gql`
      mutation ($endpoint: String!, $expirationTime: DateTime, $keys: Keys!) {
        subscribe(endpoint: $endpoint, expirationTime: $expirationTime, keys: $keys)
      }
    `;
    return sendAPIRequest(query, subscription).then(isSubscribed => {
      if (isSubscribed) {
        logInfo('PushNotifications: Saved push subscription', subscription);
      }
      else {
        const errorMessage = 'PushNotifications: Failed to save push subscription';
        logError(errorMessage, { error: subscription });
      }
    });
  };

  removeCurrentPushSubscription = () => {
    const currentPushSubscription = getFromLocalStorage('PUSH_SUBSCRIPTION', {});
    const query = gql`
      mutation ($endpoint: String!, $keys: Keys!) {
        unsubscribe(endpoint: $endpoint, keys: $keys)
      }
    `;
    if (!currentPushSubscription.subscription) {
      return Promise.resolve();
    }
    return sendAPIRequest(query, currentPushSubscription.subscription)
      .catch(e => {
        e.overrideErrorNotification({ alert: false });
        throw e;
      })
      .finally(() => {
        localStorage.removeItem('PUSH_SUBSCRIPTION');
      });
  };
}
